import { useState, type ReactNode } from 'react';

import { FieldLabel, RadioCardGroup, ToggleField } from '~/components/form';

import { DateField } from '~/components/pages/feature-flags/common';
import { fromLocalInput, toLocalInput } from '~/lib/datetime';
import type { Option } from '~/components/pages/feature-flags/common';
import type { Schedule } from '~/components/pages/feature-flags/types';

// Components only: Vite's Fast Refresh can hot-swap a module only when every export is a component (helpers live in common.ts).

/** A group of switches, one per option, for a list of things to include (§4.4). Selected values that are no longer offered stay visible so they can be removed. */
export const ValueToggles = ({
  idPrefix,
  label,
  hideLabel,
  options,
  value,
  onChange,
  emptyText,
}: {
  idPrefix: string;
  label: string;
  /** The enclosing section's title already names the group; the label stays as its accessible name. */
  hideLabel?: boolean;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  emptyText: string;
}) => {
  const stale = value.filter((v) => !options.some((o) => o.value === v));
  const shown = [...options, ...stale.map((v) => ({ value: v, label: `${v} (unavailable)` }))];
  return (
    <div>
      {!hideLabel && <FieldLabel>{label}</FieldLabel>}
      {shown.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{emptyText}</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="group" aria-label={label}>
          {shown.map((o) => (
            <ToggleField
              key={o.value}
              id={`${idPrefix}-${o.value}`}
              testId={`${idPrefix}-${o.value}`}
              inline
              text={o.label}
              checked={value.includes(o.value)}
              onChange={(on) =>
                onChange(on ? [...value, o.value] : value.filter((v) => v !== o.value))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

const STATUS_MODES = [
  { value: 'manual' as const, label: 'Manual', description: 'Enabled until you turn it off.' },
  {
    value: 'scheduled' as const,
    label: 'Scheduled',
    description: 'Enabled only between the start and end you pick.',
  },
];

/**
 * One environment's Status, shared by flags and product updates: Manual is the Enabled switch alone; Scheduled keeps
 * the switch and adds Starts and Ends beside it. The mode is remembered per `envKey` (a Scheduled pick with no dates yet
 * has nothing in `value` to show it), so switching environments and back keeps it. The Enabled switch itself sits in the
 * environment's card; while it is off this shows one line and the caller hides everything below. `aside` (product
 * updates: the user toggle and its default) sits to the right of Starts and Ends.
 */
export const EnvStatusField = <T extends { enabled: boolean } & Schedule>({
  envKey,
  envName,
  idPrefix,
  value,
  onChange,
  aside,
}: {
  envKey: string;
  envName: string;
  idPrefix: string;
  value: T;
  onChange: (next: T) => void;
  aside?: ReactNode;
}) => {
  const [modes, setModes] = useState<Record<string, 'manual' | 'scheduled'>>({});
  const mode = modes[envKey] ?? (value.startsAt || value.endsAt ? 'scheduled' : 'manual');
  const pick = (next: 'manual' | 'scheduled') => {
    setModes((m) => ({ ...m, [envKey]: next }));
    if (next === 'manual') onChange({ ...value, startsAt: null, endsAt: null });
  };

  if (!value.enabled)
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Disabled in {envName}. Turn it on in its card above to set its schedule and rules.
      </p>
    );

  return (
    <div className="space-y-4">
      <RadioCardGroup
        name={`${idPrefix}-mode`}
        ariaLabel="Status"
        value={mode}
        onChange={pick}
        options={STATUS_MODES}
      />
      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        {mode === 'scheduled' && (
          <div className="grid flex-1 grid-cols-1 gap-6 sm:grid-cols-2">
            <DateField
              id={`${idPrefix}-starts`}
              type="datetime-local"
              label="Starts"
              value={value.startsAt ? toLocalInput(value.startsAt) : ''}
              onChange={(v) => onChange({ ...value, startsAt: fromLocalInput(v) })}
              helperText="Empty starts now. Your local time."
            />
            <DateField
              id={`${idPrefix}-ends`}
              type="datetime-local"
              label="Ends"
              value={value.endsAt ? toLocalInput(value.endsAt) : ''}
              onChange={(v) => onChange({ ...value, endsAt: fromLocalInput(v) })}
              helperText="Empty never ends. Your local time."
            />
          </div>
        )}
        {aside && <div className="grid flex-1 grid-cols-1 gap-6 sm:grid-cols-2">{aside}</div>}
      </div>
    </div>
  );
};
